import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import { styles as ModalBtnsStyles } from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns.styles'
import users from '@groovehq/internal-design-system/lib/assets/images/users.png'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'

import { useDrawer } from 'ducks/drawers/hooks'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { doOpenDrawer } from 'ducks/drawers/actions'

import UserRestrictionTypeDropdown, {
  USERS_RANGES,
} from 'subapps/settings/components/UserRestrictionTypeDropdown'
import AgentsSelectionTable from 'subapps/settings/components/AgentsSelectionTable'
import TeamsSelectionTable from 'subapps/settings/components/TeamsSelectionTable'
import {
  selectCurrentChannelById,
  selectPendingChannelById,
} from 'ducks/channels/selectors'
import {
  doSaveChannelDraft,
  doClearChannelDraft,
  doUpdateChannel,
  doCreateChannel,
} from 'ducks/channels/actions'
import { doFetchMailbox } from 'actions/mailboxes'
import { selectCurrentUserId } from 'ducks/currentUser'
import { emptyArr } from 'util/arrays'
import { doBuildInboxMenuFromMailboxes } from 'ducks/folders/operations/collections'

import CONFIG from './config'
import { drawerConfig } from '../Channels.data'

const { DEFAULT_DRAWER_TITLE } = drawerConfig

const EditMembers = ({
  drawer: drawerType,
  drawerId,
  drawerResourceId: channelId,
  drawerChannelType: channelType = 'forwarding',
  onClose,
}) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(null)
  const [isSaving, setIsSaving] = useState(null)
  const [errors, setErrors] = useState(null)
  const { drawer } = CONFIG[channelType]

  const currentUserId = useSelector(selectCurrentUserId)

  const channel = useSelector(state =>
    selectCurrentChannelById(state, channelId)
  )
  const draftChannel = useSelector(state =>
    selectPendingChannelById(state, channelId)
  )

  const { contextual_restriction_type: draftRestrictionType } =
    draftChannel || {}

  // Ensure we load a fresh copy of mailbox and hydrate the draft
  // with the latest information
  useEffect(
    () => {
      if (isLoading !== null) return

      // Kevin R
      // This is what the linter said I should do
      async function loadAndSetupDraft() {
        setIsLoading(true)
        let mailbox = null
        const isNewChannel = channelId === 'new'
        if (isNewChannel && draftChannel) {
          mailbox = await dispatch(doCreateChannel(draftChannel))
        }
        const fetchedChannelId = isNewChannel ? mailbox?.id : channelId
        if (fetchedChannelId && fetchedChannelId !== 'new') {
          mailbox = await dispatch(doFetchMailbox(fetchedChannelId))
        }

        if (!mailbox) {
          onClose()
        } else if (isNewChannel) {
          dispatch(doBuildInboxMenuFromMailboxes())
          dispatch(
            doOpenDrawer(drawerId, drawerType, fetchedChannelId, {
              query: {
                ...buildDrawerQueryParam(
                  drawerId,
                  'drawerChannelType',
                  channelType
                ),
              },
            })
          )
          setIsLoading(null)
        } else {
          dispatch(
            doSaveChannelDraft(fetchedChannelId, mailbox, {
              applyUserContextRestrictionType: true,
            })
          )
          setIsLoading(false)
        }
      }
      loadAndSetupDraft()
    },
    [
      dispatch,
      channelId,
      isLoading,
      onClose,
      drawerType,
      draftChannel,
      drawerId,
      channelType,
    ]
  )

  const { group_ids: teamIds = emptyArr, user_ids: agentIds = emptyArr } =
    channel || {}

  const agentDefaultSelectedRowIds = useMemo(
    () => {
      const permission = agentIds
      return permission.reduce((selection, memberId) => {
        // eslint-disable-next-line no-param-reassign
        selection[memberId] = true
        return selection
      }, {})
    },
    [agentIds]
  )

  const teamDefaultSelectedRowIds = useMemo(
    () => {
      const permission = teamIds
      return permission.reduce((selection, memberId) => {
        // eslint-disable-next-line no-param-reassign
        selection[memberId] = true
        return selection
      }, {})
    },
    [teamIds]
  )

  const { drawerId: nextDrawerId, openDrawer: openNextDrawer } = useDrawer(
    drawer
  )

  const handleOpenNextDrawer = useCallback(
    (_, clearDraft = true) => {
      openNextDrawer(channelId, {
        query: {
          ...buildDrawerQueryParam(
            nextDrawerId,
            'drawerChannelType',
            channelType
          ),
        },
      })
      if (clearDraft) {
        dispatch(doClearChannelDraft(channelId))
      }
    },
    [dispatch, openNextDrawer, channelId, nextDrawerId, channelType]
  )

  const handleSaveAndOpenNextDrawer = useCallback(
    async e => {
      try {
        setIsSaving(true)
        setErrors(null)
        await dispatch(doUpdateChannel(channelId, draftChannel))
        setIsSaving(false)
        handleOpenNextDrawer(e, false)
      } catch (error) {
        setIsSaving(false)
        setErrors(error)
      }
    },
    [dispatch, channelId, draftChannel, handleOpenNextDrawer]
  )

  const handleClose = useCallback(
    () => {
      onClose({ ignoreStack: true })
    },
    [onClose]
  )

  const footer = useMemo(
    () => {
      return (
        <div css={ModalBtnsStyles.base}>
          <Button
            type="primary"
            onClick={handleSaveAndOpenNextDrawer}
            disabled={isSaving}
          >
            {isSaving && (
              <span>
                Saving<AnimatedEllipsis />
              </span>
            )}
            {!isSaving && <span>Add Agents</span>}
          </Button>
          {channelType === 'forwarding' ? (
            <Button type="tertiary" onClick={handleClose} disabled={isSaving}>
              Close
            </Button>
          ) : (
            <Button
              type="secondary"
              onClick={handleOpenNextDrawer}
              disabled={isSaving}
            >
              I’ll do this later
            </Button>
          )}
        </div>
      )
    },
    [
      channelType,
      handleSaveAndOpenNextDrawer,
      handleOpenNextDrawer,
      handleClose,
      isSaving,
    ]
  )

  const handleOnKeyChange = useCallback(
    type => {
      const updates = {
        restriction_type: type === 'me' ? 'users' : type,
        contextual_restriction_type: type,
      }
      if (type === USERS_RANGES.everyone) {
        updates.user_ids = []
        updates.group_ids = []
      } else if (type === USERS_RANGES.certainMembers) {
        updates.user_ids = channel.user_ids
        updates.group_ids = []
      } else if (type === USERS_RANGES.me) {
        updates.user_ids = [currentUserId]
        updates.group_ids = []
      } else if (type === USERS_RANGES.certainTeams) {
        updates.user_ids = []
        updates.group_ids = channel.group_ids
      }
      dispatch(doSaveChannelDraft(channelId, updates))
    },
    [dispatch, channelId, channel, currentUserId]
  )

  const handleAgentsOnSelectedRowIdsChange = useCallback(
    uIds => {
      const ids = Object.keys(uIds).filter(id => uIds[id])
      dispatch(doSaveChannelDraft(channelId, { user_ids: ids }))
    },
    [dispatch, channelId]
  )

  const handleTeamsOnSelectedRowIdsChange = useCallback(
    tId => {
      const ids = Object.keys(tId).filter(id => tId[id])
      dispatch(doSaveChannelDraft(channelId, { group_ids: ids }))
    },
    [dispatch, channelId]
  )

  const handleOnHide = useCallback(() => {
    setIsLoading(null)
  }, [])

  return (
    <Drawer
      title={DEFAULT_DRAWER_TITLE}
      footer={footer}
      onClose={onClose}
      onHide={handleOnHide}
      isLoading={isLoading}
    >
      <HeaderWithImage
        className="grui my-12"
        imgWidth="120px"
        img={users}
        alt="Agents"
        title="Which agents can access this mailbox?"
      />
      <MessageCard type="negative" className="grui mb-12" visible={!!errors}>
        An error occured adding the agents to your mailbox. Please try again
      </MessageCard>
      <div>
        <UserRestrictionTypeDropdown
          selectedId={draftRestrictionType}
          onKeyChange={handleOnKeyChange}
        />
        <div className="grui mt-11">
          {draftRestrictionType === USERS_RANGES.certainMembers && (
            <AgentsSelectionTable
              defaultSelectedRowIds={agentDefaultSelectedRowIds}
              onSelectedRowIdsChange={handleAgentsOnSelectedRowIdsChange}
            />
          )}
          {draftRestrictionType === USERS_RANGES.certainTeams && (
            <TeamsSelectionTable
              defaultSelectedRowIds={teamDefaultSelectedRowIds}
              onSelectedRowIdsChange={handleTeamsOnSelectedRowIdsChange}
            />
          )}
        </div>
      </div>
    </Drawer>
  )
}

export default EditMembers
